import type { User } from "@stytch/vanilla-js";
import { Link } from "react-router-dom";
import { AccountIcon, AppsIcon, ChevronIcon, SignOutIcon } from "./icons";
import { Menu, MenuDivider } from "./Menu";

/**
 * **Who this browser is signed in as, named before any family data is on screen, and the way
 * out of that account** — #104's treatment on the surface that needs it most (issue #301,
 * shaped by the #293 wireframes).
 *
 * The Stytch session is a cookie on `.iamboone.app`, so *any* earlier sign-in in this browser
 * is a session here: a shared family laptop with one parent already signed in, or somebody who
 * opened an invitation on a partner's machine. On the login app that mistake ends in a
 * refusal; here it ends in a person reading another household's memory believing it is theirs.
 *
 * **#293 weighed a per-visit disclosure gate against this and chose this**, which is what
 * makes the chip load-bearing rather than decorative: an interstitial is unmissable and costs
 * a click on every visit to a panel people open *because* something is wrong, while a chip is
 * weaker — family data is on screen while it is being read — but always true and always there.
 * Having chosen the weaker one, it may not then be hidden: it is in the header on every
 * screen, in both scopes, and at every width.
 *
 * **The menu is the other half.** Naming an account somebody cannot leave is a statement, not
 * a control, so the chip opens onto the person scope (#116's connected apps, their account)
 * and the sign-out. Deliberately absent: *Plan and billing*, which the wireframes show and the
 * product does not have — ADR 0021 decision 11 makes pricing a blocker rather than a screen.
 *
 * It discloses nothing new: whoever is sitting in front of this browser could read the address
 * by opening the login page. It is shown in the UI and never logged.
 */
export function AccountChip({ email, onSignOut }: { email: string | null; onSignOut: () => void }) {
  const shown = email ?? "an account in this browser";
  return (
    <Menu
      className="chip"
      label={`Signed in as ${shown}`}
      trigger={
        <>
          <span className="avatar" aria-hidden="true" />
          <span className="chip__value">{shown}</span>
          <ChevronIcon />
        </>
      }
    >
      <div className="menu__identity">
        <span className="avatar avatar--lg" aria-hidden="true" />
        <span className="menu__identity-names">
          <span className="menu__identity-label">Signed in as</span>
          <span className="menu__identity-value">{shown}</span>
        </span>
      </div>
      <MenuDivider />
      <Link className="menu__item" role="menuitem" to="/me/apps">
        <AppsIcon />
        <span className="menu__item-label">Connected apps</span>
      </Link>
      <Link className="menu__item" role="menuitem" to="/me/account">
        <AccountIcon />
        <span className="menu__item-label">Your account</span>
      </Link>
      <MenuDivider />
      <button
        className="menu__item menu__item--leave"
        role="menuitem"
        type="button"
        onClick={onSignOut}
      >
        <SignOutIcon />
        <span className="menu__item-names">
          <span className="menu__item-label">Sign out</span>
          {/* The consequence, under the action rather than after it. There is no narrower
              session to end: one cookie on `.iamboone.app` covers every tab and every
              first-party host, including an /authorize flow open elsewhere. */}
          <span className="menu__item-note">
            Ends the session everywhere on iamboone.app, including other tabs.
          </span>
        </span>
      </button>
    </Menu>
  );
}

/**
 * The address to show for a signed-in user.
 *
 * **Prefers a verified one**, because that is the address the server knows them by: an
 * `IDENTITY#` resolves through Stytch's verified emails, and it is what an invitation is
 * reconciled against (`services/mcp/src/auth/onboarding.ts`). Falls back to any address so the
 * account is still named rather than left blank — the same call the login app's `SignedInAs`
 * makes, and for the same reason.
 */
export function signedInEmail(user: User): string | null {
  const emails = user.emails;
  return emails.find((entry) => entry.verified)?.email ?? emails[0]?.email ?? null;
}
